import { types } from 'mobx-state-tree';
import { model, Model } from 'mst-collection';

import {
  Tutorial,
  TutorialModel,
} from '@models/Tutorials/TutorialModel';
import { enumerationNumber, safeReference } from '@models/types';
import { Course, CourseModel } from '@models/Courses/CourseModel';
import { SectionParamsModel } from './SectionParamsModel';
import {
  SectionTypeEnum,
  SectionTutorialTypeEnum,
  SectionCourseTypeEnum,
} from '@api';
import { TutorialListTypeEnum, CourseListTypeEnum } from '@constants';

export class Section extends Model({
  type: enumerationNumber([
    SectionTypeEnum.Course,
    SectionTypeEnum.Tutorial,
  ]),
  name: types.string,
  params: types.maybeNull(SectionParamsModel),
  videosType: types.maybeNull(
    enumerationNumber([
      SectionTutorialTypeEnum.Latest,
      SectionTutorialTypeEnum.Recommended,
      SectionTutorialTypeEnum.Popular,
      SectionTutorialTypeEnum.Tag,
      SectionTutorialTypeEnum.Saved,
      SectionTutorialTypeEnum.History,
    ]),
  ),
  videos: types.maybeNull(types.array(safeReference(TutorialModel))),
  coursesType: types.maybeNull(
    enumerationNumber([
      SectionCourseTypeEnum.Recommended,
      SectionCourseTypeEnum.Saved,
      SectionCourseTypeEnum.Started,
    ]),
  ),
  courses: types.maybeNull(types.array(safeReference(CourseModel))),
}) {
  get videosListType() {
    switch (this.videosType) {
      case SectionTutorialTypeEnum.Latest:
        return TutorialListTypeEnum.Latest;
      case SectionTutorialTypeEnum.Popular:
        return TutorialListTypeEnum.Popular;
      case SectionTutorialTypeEnum.Recommended:
        return TutorialListTypeEnum.Recommended;
      case SectionTutorialTypeEnum.Tag:
        return TutorialListTypeEnum.Tag;
      case SectionTutorialTypeEnum.Saved:
        return TutorialListTypeEnum.Saved;
      case SectionTutorialTypeEnum.History:
        return TutorialListTypeEnum.History;
      default:
        return TutorialListTypeEnum.Latest;
    }
  }

  get coursesListType() {
    switch (this.coursesType) {
      case SectionCourseTypeEnum.Recommended:
        return CourseListTypeEnum.Recommended;
      default:
        return CourseListTypeEnum.Recommended;
    }
  }

  get listType() {
    if (this.type === SectionTypeEnum.Course) {
      return this.coursesListType;
    }

    return this.videosListType;
  }

  get isCourseSection(): boolean {
    return this.type === SectionTypeEnum.Course;
  }

  get isTutorialSection(): boolean {
    return this.type === SectionTypeEnum.Tutorial;
  }

  get content(): {
    type: SectionCourseTypeEnum | SectionTutorialTypeEnum;
    data: Course[] | Tutorial[];
  } {
    if (this.isCourseSection) {
      return {
        type: this.coursesType!,
        data: this.courses!,
      };
    }

    return {
      type: this.videosType!,
      data: this.videos!,
    };
  }

  get preview() {
    return this.content.data.slice(0, 10);
  }
}

export const SectionModel = model(Section);
